import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import Badge from '../../components/ui/Badge';
import { PrinterIcon } from '../../components/ui';
import { formatDate } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { ReportTextCell, CrewDataTableFrame, CrewReportViewAction, useCrewReportOpen } from './reportShared';
import {
  formatStatNum,
  StatsBucketChart,
  StatsBucketHeader,
  StatsCountCell,
  StatsKpiListLayout,
  StatsMixPanel,
  StatsPageShell,
  StatsRankedBars,
} from './statisticsShared';
import { nrCountPrintPath } from './reports/nrCountPrintMeta';
import type { CrewStatMixItem, CrewStatsNrRow } from './types';

export const NR_AVG_BUCKETS = [
  { key: 'lt2', label: '< 2', test: (n: number) => n < 2 },
  { key: 'a2_3', label: '2–3', test: (n: number) => n >= 2 && n < 3 },
  { key: 'a3_4', label: '3–4', test: (n: number) => n >= 3 && n < 4 },
  { key: 'a4', label: '≥ 4', test: (n: number) => n >= 4 },
] as const;

const NR_BAR: Record<string, string> = {
  lt2: 'bg-rose-500',
  a2_3: 'bg-amber-500',
  a3_4: 'bg-indigo-500',
  a4: 'bg-sky-500',
  unknown: 'bg-slate-400',
};

const NR_BAR_BY_LABEL: Record<string, string> = {
  '< 2': NR_BAR.lt2,
  '2–3': NR_BAR.a2_3,
  '3–4': NR_BAR.a3_4,
  '≥ 4': NR_BAR.a4,
  'No appraisal': NR_BAR.unknown,
};

type NrAvgBucketKey = (typeof NR_AVG_BUCKETS)[number]['key'] | 'unknown';

export type NrCountRow = {
  key: string;
  lastVessel: string;
  nationality: string;
  lt2: number;
  a2_3: number;
  a3_4: number;
  a4: number;
  unknown: number;
  total: number;
};

function avgBucket(avg: number | null | undefined): NrAvgBucketKey {
  if (avg == null || !Number.isFinite(avg)) return 'unknown';
  const match = NR_AVG_BUCKETS.find((b) => b.test(avg));
  return match?.key ?? 'unknown';
}

export function buildNrCountRows(rows: CrewStatsNrRow[]): NrCountRow[] {
  const map = new Map<string, NrCountRow>();
  for (const row of rows) {
    const lastVessel = row.lastVessel?.trim() || '—';
    const nationality = row.nationality?.trim() || 'Unknown';
    const key = `${lastVessel}\0${nationality}`;
    let agg = map.get(key);
    if (!agg) {
      agg = {
        key,
        lastVessel,
        nationality,
        lt2: 0,
        a2_3: 0,
        a3_4: 0,
        a4: 0,
        unknown: 0,
        total: 0,
      };
      map.set(key, agg);
    }
    agg[avgBucket(row.appraisalAvg)] += 1;
    agg.total += 1;
  }
  return Array.from(map.values()).sort((a, b) => {
    const byVessel = a.lastVessel.localeCompare(b.lastVessel, undefined, { sensitivity: 'base' });
    if (byVessel !== 0) return byVessel;
    return a.nationality.localeCompare(b.nationality, undefined, { sensitivity: 'base' });
  });
}

export function avgMixFromNrCountRows(rows: NrCountRow[]): CrewStatMixItem[] {
  const items: CrewStatMixItem[] = NR_AVG_BUCKETS.map((b) => ({
    label: b.label,
    count: rows.reduce((s, r) => s + r[b.key], 0),
  }));
  const unknown = rows.reduce((s, r) => s + r.unknown, 0);
  if (unknown > 0) items.push({ label: 'No appraisal', count: unknown });
  return items;
}

export function nationalityMixFromNrCountRows(rows: NrCountRow[]): CrewStatMixItem[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    map.set(row.nationality, (map.get(row.nationality) ?? 0) + row.total);
  }
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

const NrMixPanel: React.FC<{
  nrCount: number;
  vesselCount: number;
  nationalityCount: number;
  avgMix: CrewStatMixItem[];
  nationalityMix: CrewStatMixItem[];
}> = ({ nrCount, vesselCount, nationalityCount, avgMix, nationalityMix }) => {
  const { colors } = useTheme();
  return (
    <StatsMixPanel
      metrics={[
        { label: 'Not recommended', value: formatStatNum(nrCount) },
        { label: 'Last vessels', value: formatStatNum(vesselCount) },
        { label: 'Nationalities', value: formatStatNum(nationalityCount) },
      ]}
    >
      <StatsBucketChart
        title="Appraisal average"
        hint="Questionnaire avg"
        data={avgMix}
        total={nrCount}
        emptyLabel="No not-recommended crew"
        barClassFor={(label) => NR_BAR_BY_LABEL[label] ?? NR_BAR.unknown}
      />
      <div className={cn('shrink-0 border-t', colors.border)} />
      <StatsRankedBars
        title="Nationality mix"
        hint={
          nationalityMix.length
            ? `${nationalityMix.length} ${nationalityMix.length === 1 ? 'nationality' : 'nationalities'}`
            : undefined
        }
        data={nationalityMix}
        total={nrCount}
        emptyLabel="No not-recommended crew"
        barClass="bg-rose-500/90"
      />
    </StatsMixPanel>
  );
};

const NotRecommendedStatisticsPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const [listTab, setListTab] = useState<'summary' | 'crew'>('summary');

  const summaryColumns: Column<NrCountRow>[] = useMemo(() => {
    const avgCols: Column<NrCountRow>[] = NR_AVG_BUCKETS.map((b) => ({
      key: b.key,
      header: <StatsBucketHeader colorClass={NR_BAR[b.key]} label={b.label} />,
      sortable: true,
      align: 'right' as const,
      footerType: 'sum' as const,
      columnGroup: { id: 'avg', header: 'Appraisal average' },
      render: (v: unknown) => <StatsCountCell value={v} emphasize={b.key === 'lt2'} />,
    }));
    return [
      {
        key: 'lastVessel',
        header: 'Last vessel',
        sortable: true,
        render: (v: string) => <ReportTextCell value={v} />,
      },
      {
        key: 'nationality',
        header: 'Nationality',
        sortable: true,
        render: (v: string) => <ReportTextCell value={v} />,
      },
      ...avgCols,
      {
        key: 'unknown',
        header: <StatsBucketHeader colorClass={NR_BAR.unknown} label="No appraisal" />,
        sortable: true,
        align: 'right',
        footerType: 'sum',
        columnGroup: { id: 'avg', header: 'Appraisal average' },
        render: (v: unknown) => <StatsCountCell value={v} />,
      },
      {
        key: 'total',
        header: 'Total',
        sortable: true,
        align: 'right',
        footerType: 'sum',
        render: (v: number) => (
          <span className={cn('tabular-nums font-semibold', colors.text)}>{v}</span>
        ),
      },
    ];
  }, [colors.text]);

  const crewColumns: Column<CrewStatsNrRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Crew',
        sortable: true,
        render: (_v, row) => (
          <Link
            to={`/crew/${row.crewId}`}
            state={{ from: listReturnPath }}
            className={cn('text-sm hover:underline', colors.primaryFg)}
            onClick={(e) => e.stopPropagation()}
          >
            {row.name}
          </Link>
        ),
      },
      { key: 'crewRef', header: 'Ref', sortable: true, render: (v: string | null | undefined) => <ReportTextCell value={v} /> },
      { key: 'rank', header: 'Rank', sortable: true, render: (v: string | null) => <ReportTextCell value={v} /> },
      {
        key: 'nationality',
        header: 'Nationality',
        sortable: true,
        render: (v: string | null) => <ReportTextCell value={v} />,
      },
      { key: 'age', header: 'Age', sortable: true, render: (v: number | null | undefined) => <ReportTextCell value={v} /> },
      {
        key: 'lastAppraisalDate',
        header: 'Last appraisal',
        sortable: true,
        render: (v: string | null | undefined) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'appraisalAvg',
        header: 'Appraisal avg',
        sortable: true,
        align: 'right',
        render: (v: number | null | undefined) => {
          const low = typeof v === 'number' && v < 2;
          return (
            <span className={cn(low ? 'font-medium text-rose-600 dark:text-rose-400' : undefined)}>
              <ReportTextCell
                value={
                  v != null && typeof v === 'number'
                    ? v.toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : null
                }
              />
            </span>
          );
        },
      },
      {
        key: 'overallEvaluation',
        header: 'Evaluation',
        sortable: true,
        render: (v: string | null | undefined) => <ReportTextCell value={v} />,
      },
      {
        key: 'toBePromoted',
        header: 'Promote',
        sortable: true,
        render: (v: boolean | undefined) =>
          v ? (
            <Badge variant="success" className="rounded-md px-2 py-0.5">
              Yes
            </Badge>
          ) : (
            <ReportTextCell value="" />
          ),
      },
      {
        key: 'lastVessel',
        header: 'Last vessel',
        sortable: true,
        render: (v: string | null | undefined) => <ReportTextCell value={v} />,
      },
      {
        key: 'lastSignOffDate',
        header: 'Last S/Off',
        sortable: true,
        render: (v: string | null | undefined) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
    ],
    [listReturnPath, colors.primaryFg],
  );

  return (
    <StatsPageShell
      hintTitle="Not recommended"
      hintBody="Count of not-recommended crew per last vessel, nationality, and appraisal average."
      filterMode="vessel"
      trailingActions={({ applied }) =>
        applied ? (
          <Link
            to={nrCountPrintPath({ vesselId: applied.vesselId })}
            title="Print preview"
            className={cn(
              'inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors',
              colors.border,
              colors.text,
              'hover:bg-black/5 dark:hover:bg-white/10',
            )}
          >
            <PrinterIcon className="h-4 w-4" />
            Print
          </Link>
        ) : null
      }
    >
      {({ data }) => {
        const nr = data.lists?.notRecommended ?? [];
        const rows = buildNrCountRows(nr);
        const vesselCount = new Set(rows.map((r) => r.lastVessel)).size;
        const nationalityCount = new Set(rows.map((r) => r.nationality)).size;
        const showUnknown = rows.some((r) => r.unknown > 0);
        const columns = showUnknown
          ? summaryColumns
          : summaryColumns.filter((c) => c.key !== 'unknown');
        const avgMix = avgMixFromNrCountRows(rows);
        const nationalityMix = nationalityMixFromNrCountRows(rows);

        return (
          <StatsKpiListLayout
            charts={
              <NrMixPanel
                nrCount={nr.length}
                vesselCount={vesselCount}
                nationalityCount={nationalityCount}
                avgMix={avgMix}
                nationalityMix={nationalityMix}
              />
            }
            chartsPlacement="right"
            tabs={[
              { id: 'summary', label: 'Counts', count: rows.length },
              { id: 'crew', label: 'Crew', count: nr.length },
            ]}
            activeTab={listTab}
            onTabChange={(id) => setListTab(id === 'crew' ? 'crew' : 'summary')}
          >
            <CrewDataTableFrame>
              {listTab === 'summary' ? (
                <DataTable
                  data={rows}
                  columns={columns}
                  keyExtractor={(row) => row.key}
                  emptyMessage="No not-recommended crew"
                  showFooter={rows.length > 0}
                />
              ) : (
                <DataTable
                  data={nr}
                  columns={crewColumns}
                  keyExtractor={(row) => row.crewId}
                  emptyMessage="No not-recommended crew"
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              )}
            </CrewDataTableFrame>
          </StatsKpiListLayout>
        );
      }}
    </StatsPageShell>
  );
};

export default NotRecommendedStatisticsPage;
